/**
 * Reasoning Log Types and Utilities
 * 
 * Shared types and helper functions for working with reasoning logs
 * across SSE streaming and REST API.
 */

/**
 * Reasoning log entry structure (matches backend OpenAPI schema)
 * 
 * Note: The backend uses this structure in both SSE events and REST API responses.
 * SSE sends ISO string dates, while the OpenAPI client deserializes to Date objects.
 * This type uses string dates to match the wire format.
 */
export interface ReasoningLogEntry {
  id: number;
  activityName: string;
  reasoningContent: string;
  tokenCount?: number | null;
  createdAt: string;
}

/**
 * Log category types for organizing reasoning logs
 */
export type LogCategory = 'extraction' | 'level' | 'room' | 'line_item' | 'general';

/**
 * Configuration for log categories (icons, labels, colors)
 */
export interface LogCategoryConfig {
  label: string;
  color: string;
}

/**
 * Normalize a log entry from any source to standard format
 * Handles Date objects, string dates, and missing fields
 */
export function normalizeLogEntry(log: any, fallbackId: number = 0): ReasoningLogEntry {
  // Safely convert createdAt to ISO string
  let createdAtString: string;
  try {
    if (log.createdAt instanceof Date) {
      // Check if date is valid before calling toISOString
      if (isNaN(log.createdAt.getTime())) {
        createdAtString = new Date().toISOString();
      } else {
        createdAtString = log.createdAt.toISOString();
      }
    } else if (typeof log.createdAt === 'string') {
      createdAtString = log.createdAt;
    } else {
      createdAtString = new Date().toISOString();
    }
  } catch (error) {
    console.warn('Failed to parse createdAt for log:', log, error);
    createdAtString = new Date().toISOString();
  }

  return {
    id: log.id ?? fallbackId,
    activityName: log.activityName ?? 'unknown',
    reasoningContent: log.reasoningContent ?? '',
    tokenCount: log.tokenCount,
    createdAt: createdAtString
  };
}

/**
 * Convert SSE string logs to ReasoningLogEntry format
 */
export function normalizeSSELogs(sseLogs: string[]): ReasoningLogEntry[] {
  return sseLogs.map((content, index) => ({
    id: index,
    activityName: 'reasoning',
    reasoningContent: content,
    createdAt: new Date().toISOString()
  }));
}

/**
 * Normalize array of log entries from API or other sources
 */
export function normalizeLogs(logs: any[]): ReasoningLogEntry[] {
  return logs.map((log, index) => normalizeLogEntry(log, index));
}

/**
 * Categorize a single log entry based on activity name and content
 */
export function categorizeLog(log: ReasoningLogEntry): LogCategory {
  const lowerContent = log.reasoningContent?.toLowerCase() || '';
  const lowerActivity = log.activityName?.toLowerCase() || '';

  // Categorize based on activity name first (higher priority)
  if (lowerActivity.includes('extract')) {
    return 'extraction';
  } else if (lowerActivity.includes('level')) {
    return 'level';
  } else if (lowerActivity.includes('room')) {
    return 'room';
  } else if (lowerActivity.includes('line') || lowerActivity.includes('item')) {
    return 'line_item';
  }
  // Fallback to content keywords if activity name is not determinant
  else if (lowerContent.includes('extraction') || lowerContent.includes('parsing')) {
    return 'extraction';
  } else if (lowerContent.includes('level') || lowerContent.includes('floor')) {
    return 'level';
  } else if (lowerContent.includes('room') || lowerContent.includes('space')) {
    return 'room';
  } else if (lowerContent.includes('line item') || lowerContent.includes('matching items')) {
    return 'line_item';
  } else {
    return 'general';
  }
}

/**
 * Group logs by category
 */
export function categorizeLogs(logs: ReasoningLogEntry[]): Record<LogCategory, ReasoningLogEntry[]> {
  const categories: Record<LogCategory, ReasoningLogEntry[]> = {
    extraction: [],
    level: [],
    room: [],
    line_item: [],
    general: []
  };

  logs.forEach((log) => {
    const category = categorizeLog(log);
    categories[category].push(log);
  });

  return categories;
}

/**
 * Get categorized logs as array of tuples, filtering out empty categories
 */
export function getCategorizedLogsArray(logs: ReasoningLogEntry[]): [LogCategory, ReasoningLogEntry[]][] {
  const categories = categorizeLogs(logs);
  return Object.entries(categories).filter(([, logs]) => logs.length > 0) as [LogCategory, ReasoningLogEntry[]][];
}
